import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './AdminManageCategories.css';
import { buildCategoryTree } from '../../firebase/categories';
import { useCategoryManagement } from '../../hooks/useCategoryManagement';
import { useProductManagement } from '../../hooks/useProductManagement';

function ProductTabBar({ active }) {
    const navigate = useNavigate();
    const tabs = [
        { label: 'Manage Products', path: '/admin/products/manage' },
        { label: 'Manage Category', path: '/admin/products/categories' },
        { label: 'Manage Subcategory', path: '/admin/products/subcategories' },
        { label: 'Manage Submenus', path: '/admin/products/submenus' },
        { label: 'Manage Attributes', path: '/admin/products/attributes' },
        { label: 'Manage Inventory', path: '/admin/products/inventory' },
    ];
    return (
        <div className="mc-tab-bar">
            {tabs.map((tab) => (
                <button
                    key={tab.path}
                    className={`mc-tab${active === tab.label ? ' mc-tab--active' : ''}`}
                    onClick={() => navigate(tab.path)}
                >
                    {tab.label}
                </button>
            ))}
        </div>
    );
}

function StatusToggle({ status, onChange }) {
    const checked = status === 'active';
    return (
        <label className="mc-toggle">
            <input type="checkbox" checked={checked} onChange={onChange} />
            <span className="mc-toggle-track"><span className="mc-toggle-thumb" /></span>
        </label>
    );
}

function StatusBadge({ status }) {
    return <span className={`mc-status-badge mc-status-badge--${status || 'active'}`}>{status === 'inactive' ? 'Inactive' : 'Active'}</span>;
}

function SubmenuDraftModal({ subcategory, initialSubmenus = [], onClose, onSave }) {
    const [name, setName] = useState('');
    const [submenus, setSubmenus] = useState(initialSubmenus);

    const add = () => {
        const value = name.trim();
        if (!value || submenus.some((item) => item.name.toLowerCase() === value.toLowerCase())) return;
        setSubmenus((prev) => [...prev, { name: value, status: 'active' }]);
        setName('');
    };

    const toggleStatus = (index) => {
        setSubmenus((prev) => prev.map((item, i) => i === index ? { ...item, status: item.status === 'active' ? 'inactive' : 'active' } : item));
    };

    return (
        <div className="mc-modal-overlay" onClick={onClose}>
            <div className="mc-dialog mc-dialog--wide" onClick={(event) => event.stopPropagation()}>
                <div className="mc-modal-header">
                    <h2 className="mc-modal-title">Add Submenus to: {subcategory.name}</h2>
                    <button className="mc-modal-close" onClick={onClose}>×</button>
                </div>
                <div className="mc-field">
                    <label>Submenu Name</label>
                    <div className="mc-sub-input-row">
                        <input value={name} onChange={(event) => setName(event.target.value)} onKeyDown={(event) => event.key === 'Enter' && add()} placeholder="e.g. Round Neck" />
                        <button type="button" className="mc-sub-add-btn" onClick={add}>Add</button>
                    </div>
                </div>
                <div className="mc-structured-list">
                    {submenus.length === 0 ? (
                        <p className="mc-sub-empty">No submenus added yet.</p>
                    ) : submenus.map((submenu, index) => (
                        <div key={`${submenu.name}-${index}`} className="mc-tree-item mc-tree-item--submenu">
                            <span>{submenu.name}</span>
                            <StatusToggle status={submenu.status} onChange={() => toggleStatus(index)} />
                            <button type="button" className="mc-mini-danger" onClick={() => setSubmenus((prev) => prev.filter((_, i) => i !== index))}>Delete</button>
                        </div>
                    ))}
                </div>
                <div className="mc-modal-footer">
                    <button className="mc-btn-secondary" onClick={onClose}>Cancel</button>
                    <button className="mc-btn-primary" onClick={() => onSave(submenus)}>Save Submenus</button>
                </div>
            </div>
        </div>
    );
}

function CategoryModal({ category, allSubcategories = [], onClose, onSave }) {
    const isEdit = Boolean(category);
    const [name, setName] = useState(category?.name || '');
    const [status, setStatus] = useState(category?.status || 'active');
    const [subInput, setSubInput] = useState('');
    const [subcategories, setSubcategories] = useState(
        isEdit ? allSubcategories.filter((sub) => sub.parentId === category?.id).map((sub) => ({ name: sub.name, status: sub.status, submenus: sub.submenus || [] })) : []
    );
    const [submenuTarget, setSubmenuTarget] = useState(null);

    const addSubcategory = () => {
        const value = subInput.trim();
        if (!value || subcategories.some((item) => item.name.toLowerCase() === value.toLowerCase())) return;
        setSubcategories((prev) => [...prev, { name: value, status: 'active', submenus: [] }]);
        setSubInput('');
    };

    const toggleSubcategoryStatus = (index) => {
        setSubcategories((prev) => prev.map((item, i) => i === index ? { ...item, status: item.status === 'active' ? 'inactive' : 'active' } : item));
    };

    const submit = () => {
        if (!name.trim()) return;
        onSave({
            name: name.trim(),
            status,
            subcategories: isEdit ? undefined : subcategories.map((sub) => sub.name),
        });
    };

    return (
        <div className="mc-modal-overlay" onClick={onClose}>
            <div className="mc-modal" onClick={(event) => event.stopPropagation()}>
                <div className="mc-modal-header">
                    <h2 className="mc-modal-title">{isEdit ? 'Edit Category' : 'Add Category'}</h2>
                    <button className="mc-modal-close" onClick={onClose}>×</button>
                </div>
                <div className="mc-modal-body">
                    <div className="mc-field">
                        <label>Category Name <span className="mc-required">*</span></label>
                        <input type="text" value={name} onChange={(event) => setName(event.target.value)} placeholder="e.g. Clothing" />
                    </div>
                    <div className="mc-field">
                        <label>Status</label>
                        <StatusToggle status={status} onChange={() => setStatus(status === 'active' ? 'inactive' : 'active')} />
                    </div>
                    {isEdit && allSubcategories.filter((sub) => sub.parentId === category?.id).length > 0 && (
                        <div className="mc-field">
                            <label>Subcategories</label>
                            <div className="mc-edit-cat-chips">
                                {allSubcategories.filter((sub) => sub.parentId === category?.id).map((subcategory) => (
                                    <div key={subcategory.id} className="mc-edit-cat-chip">
                                        <span className="mc-edit-cat-chip-name">{subcategory.name}</span>
                                        <span className="mc-edit-cat-chip-count">{subcategory.submenus?.length || 0}</span>
                                        <StatusBadge status={subcategory.status} />
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                    {!isEdit && (
                        <div className="mc-field">
                            <label>Subcategories</label>
                            <div className="mc-sub-input-row">
                                <input value={subInput} onChange={(event) => setSubInput(event.target.value)} onKeyDown={(event) => event.key === 'Enter' && addSubcategory()} placeholder="e.g. T-shirt" />
                                <button type="button" className="mc-sub-add-btn" onClick={addSubcategory}>Add</button>
                            </div>
                            <div className="mc-structured-list">
                                {subcategories.length === 0 ? (
                                    <p className="mc-sub-empty">No subcategories added yet.</p>
                                ) : subcategories.map((subcategory, index) => (
                                    <div key={subcategory.name} className="mc-tree-item">
                                        <span className="mc-tree-name">{subcategory.name} ({subcategory.submenus.length})</span>
                                        <StatusToggle status={subcategory.status} onChange={() => toggleSubcategoryStatus(index)} />
                                        <button type="button" className="mc-mini-btn" onClick={() => setSubmenuTarget({ ...subcategory, index })}>Add Submenu</button>
                                        <button type="button" className="mc-mini-danger" onClick={() => setSubcategories((prev) => prev.filter((_, i) => i !== index))}>×</button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
                <div className="mc-modal-footer">
                    <button className="mc-btn-secondary" onClick={onClose}>Cancel</button>
                    <button className="mc-btn-primary" onClick={submit}>{isEdit ? 'Update Category' : 'Create Category'}</button>
                </div>
                {submenuTarget && (
                    <SubmenuDraftModal
                        subcategory={submenuTarget}
                        initialSubmenus={submenuTarget.submenus}
                        onClose={() => setSubmenuTarget(null)}
                        onSave={(items) => {
                            setSubcategories((prev) => prev.map((item, index) => index === submenuTarget.index ? { ...item, submenus: items } : item));
                            setSubmenuTarget(null);
                        }}
                    />
                )}
            </div>
        </div>
    );
}

function DeleteDialog({ category, onClose, onConfirm }) {
    return (
        <div className="mc-modal-overlay" onClick={onClose}>
            <div className="mc-dialog" onClick={(event) => event.stopPropagation()}>
                <h3 className="mc-dialog-title">Delete Category</h3>
                <p className="mc-dialog-body">Delete <strong>{category.name}</strong>? Child records should be reviewed before removing this item.</p>
                <div className="mc-dialog-actions">
                    <button className="mc-btn-secondary" onClick={onClose}>Cancel</button>
                    <button className="mc-btn-danger" onClick={onConfirm}>Delete</button>
                </div>
            </div>
        </div>
    );
}

export default function AdminManageCategories() {
    const {
        categories,
        loading,
        error,
        fetchCategories,
        createCategoryEntry,
        updateCategoryEntry,
        deleteCategoryEntry,
    } = useCategoryManagement([]);
    const { products, fetchProducts } = useProductManagement([]);
    const [search, setSearch] = useState('');
    const [modal, setModal] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [expanded, setExpanded] = useState({});
    const [actionError, setActionError] = useState(null);
    const [statusFilter, setStatusFilter] = useState('all');
    const [pageSize, setPageSize] = useState(10);
    const [page, setPage] = useState(1);
    const [sortBy, setSortBy] = useState('name-asc');
    const [filterLevel, setFilterLevel] = useState('all');

    useEffect(() => {
        fetchCategories();
        fetchProducts();
    }, [fetchCategories, fetchProducts]);

    const tree = useMemo(() => buildCategoryTree(categories), [categories]);
    const productCounts = useMemo(() => {
        return products.reduce((counts, product) => {
            const key = product.category || '';
            counts[key] = (counts[key] || 0) + 1;
            return counts;
        }, {});
    }, [products]);
    
    const allSubcategories = useMemo(() => 
        tree.flatMap((main) => main.subcategories.map((sub) => ({ ...sub, parentName: main.name })))
    , [tree]);

    const filteredTree = useMemo(() => {
        const q = search.trim().toLowerCase();
        let result = tree;
        if (statusFilter !== 'all') {
            result = result.filter((main) => main.status === statusFilter);
        }
        if (!q) return result;
        return result.filter((main) =>
            main.name.toLowerCase().includes(q) ||
            main.subcategories.some((sub) => sub.name.toLowerCase().includes(q) || sub.submenus.some((submenu) => submenu.name.toLowerCase().includes(q)))
        );
    }, [search, tree, statusFilter]);

    const saveCategory = async (payload) => {
        setActionError(null);
        const result = modal?.id
            ? await updateCategoryEntry(modal.id, payload)
            : await createCategoryEntry(payload);
        if (result.error) {
            setActionError(result.error);
            return;
        }
        setModal(null);
    };

    const deleteCategory = async () => {
        setActionError(null);
        const result = await deleteCategoryEntry(deleteTarget.id);
        if (result.error) {
            setActionError(result.error);
            return;
        }
        setDeleteTarget(null);
    };

    const hasActiveFilters = statusFilter !== 'all' || search.trim() !== '';

    return (
        <div className="mc-page">
            <ProductTabBar active="Manage Category" />
            {loading && <div className="mc-info-banner">Loading categories...</div>}
            {(error || actionError) && <div className="mc-info-banner">{error || actionError}</div>}

            <div className="mc-toolbar">
                <div className="mc-toolbar-left">
                    <div className="mc-entries-label">
                        Show
                        <select className="mc-entries-select" value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
                            <option value="all">All Statuses</option>
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                        </select>
                    </div>
                    {hasActiveFilters && (
                        <button className="mc-filter-clear" onClick={() => { setStatusFilter('all'); setSearch(''); }}>Clear filters</button>
                    )}
                </div>
                <div className="mc-toolbar-right">
                    <div className="mc-search-wrap">
                        <input className="mc-search" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search hierarchy..." />
                    </div>
                    <button className="mc-btn-add" onClick={() => setModal('add')}>Add New Category</button>
                </div>
            </div>

            <div className="mc-table-wrap">
                <table className="mc-table">
                    <thead>
                        <tr>
                            <th></th>
                            <th>Name</th>
                            <th>Level</th>
                            <th>Products</th>
                            <th>Children</th>
                            <th>Status</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredTree.length === 0 ? (
                            <tr><td colSpan={7} className="mc-empty">No categories found.</td></tr>
                        ) : filteredTree.map((main) => (
                            <React.Fragment key={main.id}>
                                <tr>
                                    <td>
                                        <button className={`mc-expand-btn${expanded[main.id] ? ' mc-expand-btn--open' : ''}`} onClick={() => setExpanded((prev) => ({ ...prev, [main.id]: !prev[main.id] }))}>⌄</button>
                                    </td>
                                    <td className="mc-cat-name">{main.name}</td>
                                    <td>Main Category</td>
                                    <td>{productCounts[main.name] || 0}</td>
                                    <td>{main.subcategories.length} subcategories</td>
                                    <td><StatusBadge status={main.status} /></td>
                                    <td>
                    <div className="mc-actions">
                        <button className="mc-action-btn mc-action-btn--edit" onClick={() => setModal(main)} title="Edit">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                        </button>
                        {!main.isLegacy && <button className="mc-action-btn mc-action-btn--delete" onClick={() => setDeleteTarget(main)} title="Delete">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
                        </button>}
                    </div>
                </td>
                                </tr>
                                {expanded[main.id] && main.subcategories.map((subcategory) => (
                                    <tr key={subcategory.id} className="mc-hierarchy-row">
                                        <td></td>
                                        <td className="mc-indent-name">└ {subcategory.name}</td>
                                        <td>Subcategory</td>
                                        <td>{products.filter((product) => product.subcategory === subcategory.name).length}</td>
                                        <td>{subcategory.submenus.length} submenus</td>
                                        <td><StatusBadge status={subcategory.status} /></td>
                                        <td>{subcategory.isLegacy ? 'Legacy array item' : 'Managed in Subcategories'}</td>
                                    </tr>
                                ))}
                            </React.Fragment>
                        ))}
                    </tbody>
                </table>
            </div>

            {modal && (
                <CategoryModal category={modal === 'add' ? null : modal} allSubcategories={allSubcategories} onClose={() => setModal(null)} onSave={saveCategory} />
            )}
            {deleteTarget && <DeleteDialog category={deleteTarget} onClose={() => setDeleteTarget(null)} onConfirm={deleteCategory} />}
        </div>
    );
}
